import { useState, useEffect, MutableRefObject } from 'react';

import { VIEWER_ONLY, APPLY_WATERMARK } from '../../../../consts';
import { captureServerError } from '../../../utils';
import { useStores } from '../../../hooks';
import PolicyStore from '../../../../stores/PolicyStore';
import appConfig from '../../../../config/env';
import { loadDefaultWatermark, loadImage } from '../helpers/imageUtils';

const tryLoadLogo = async (): Promise<HTMLImageElement> => {
    try {
        return await loadImage(appConfig.HEADER_COMPANY_LOGO);
    } catch (error) {
        captureServerError(error as Error, 'PdfTileViewer.tryLoadLogo');
        return loadDefaultWatermark();
    }
};

export const tryCheckHasWatermarkPolicy = async (policyId: string, policyStore: PolicyStore): Promise<boolean> => {
    const policy = await policyStore.tryFindPolicy(policyId);
    if (policy) {
        const accessList = policy.settings?.access;
        const parentViewerPolicy = accessList?.find(({ id }) => id === VIEWER_ONLY);
        return !!(parentViewerPolicy?.value)
            && !!(accessList?.find(({ id }) => id === APPLY_WATERMARK)?.value);
    }
    return false;
};

export interface Watermarks {
    logo: HTMLImageElement;
    email: string;
}

interface UseWatermarksParams {
    filePolicyId: string;
    isMountedRef: MutableRefObject<boolean>;
    setIsLoading: (value: boolean) => void;
}

interface UseWatermarksResult {
    watermarks: Watermarks | null;
    hasFailed: boolean;
}

const useWatermarks = ({
    setIsLoading,
    filePolicyId,
    isMountedRef,
}: UseWatermarksParams): UseWatermarksResult => {
    const [watermarks, setWatermarks] = useState<Watermarks>(null);
    const [hasFailed, setHasFailed] = useState<boolean>(false);

    const { userStore, policyStore } = useStores();

    useEffect(() => {
        let cancelled = false;

        (async (): Promise<void> => {
            setIsLoading(true);
            setHasFailed(false);
            try {
                const [logo, hasWatermarkPolicy] = await Promise.all([
                    tryLoadLogo(), tryCheckHasWatermarkPolicy(filePolicyId, policyStore),
                ]);
                if (isMountedRef.current && !cancelled) {
                    setWatermarks({
                        logo,
                        email: hasWatermarkPolicy ? userStore.currentUserEmail : null,
                    });
                    setIsLoading(false);
                }
            } catch (error) {
                captureServerError(error as Error, 'PdfTileViewer.useWatermarks');
                if (isMountedRef.current && !cancelled) {
                    setHasFailed(true);
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [filePolicyId, userStore.currentUserEmail]);

    return { watermarks, hasFailed };
};

export default useWatermarks;
